import type React from 'react';

import { Image, Text } from '@change/ds-core-ui';
import { WarningIcon } from '@change/ds-icons';

import { useGetStaticAssetUrl } from '@change-corgi/core-react/assets';
import { Translate, useI18n } from '@change-corgi/core-react/i18n';

import { EmbeddedMedia } from '#src/app/shared/components/embeddedMedia';

import type { MediaHitEmbed } from '../../../shared/context';
import { useMediaHitsPermissions } from '../../../shared/hooks/useMediaHitsPermissions';
import { DeleteMediaHit } from '../../DeleteMediaHit';

type MediaHitProps = {
	media: MediaHitEmbed;
	removeMedia: (mediaId: string) => void;
};

const InvalidMediaHit: React.FC<MediaHitProps> = ({ media, removeMedia }) => {
	const { translate } = useI18n();

	return (
		<div
			data-testid={`media-hit-${media.id}`}
			className="w-[300px] shrink-0 snap-start scroll-ml-6 md:w-[500px] md:snap-align-none md:scroll-ml-0"
		>
			<div className="mt-[42px] flex h-[110px] rounded-lg bg-[#403a490d] p-4 text-black md:h-[156px]">
				<WarningIcon size="small" className="mt-0.5 mr-2 shrink-0 text-yellow-400" />
				<div className="flex h-full min-w-0 flex-1 flex-col justify-between overflow-hidden">
					<Text variant="heading" size="xx-small" truncate>
						{translate('corgi.petition_gamma.media_mentions.invalid.title')}
					</Text>
					<Text as="p" size="small" truncate>
						<Translate value="corgi.petition_gamma.media_mentions.invalid.message" />
					</Text>
					<Text as="p" size="small" className="line-clamp-2 text-wrap">
						URL: {media.url}
					</Text>
				</div>
			</div>
			<div className="my-2">
				<DeleteMediaHit media={media} removeMedia={removeMedia} />
			</div>
		</div>
	);
};

const ValidMediaHit: React.FC<MediaHitProps> = ({ media, removeMedia }) => {
	const getStaticAssetUrl = useGetStaticAssetUrl();
	const isAutomated = media.mediaSource === 'AUTOMATED';

	return (
		<div
			data-testid={`media-hit-${media.id}`}
			className="w-[300px] shrink-0 snap-start scroll-ml-6 md:w-[500px] md:snap-align-none md:scroll-ml-0"
		>
			<div className="flex h-[42px] items-center p-2">
				{isAutomated && (
					<>
						<Image src={getStaticAssetUrl('/campaign_media_hits/stars_red.svg')} alt="" className="mr-2" />
						<Text size="small">
							<Translate value="corgi.petition_gamma.media_mentions.valid.detected_by_change" />
						</Text>
					</>
				)}
			</div>
			<EmbeddedMedia html={media.html} aspectRatio={16 / 9} />
			<div className="my-2">
				<DeleteMediaHit media={media} removeMedia={removeMedia} />
			</div>
		</div>
	);
};

export const MediaHit: React.FC<MediaHitProps> = ({ media, removeMedia }) => {
	const {
		data: { canDelete },
	} = useMediaHitsPermissions();

	// Only show invalid media if user can delete
	if (!media.valid && !canDelete) {
		return null;
	}

	if (!media.valid) {
		return <InvalidMediaHit media={media} removeMedia={removeMedia} />;
	}

	return <ValidMediaHit media={media} removeMedia={removeMedia} />;
};
